/**
 * The webinar pages have their own host, live.uncoveringintimacy.com (decided
 * 2026-10-14; backend docs/webinar.md "The live host"). It serves this same
 * build: index.tsx sees a hostname starting with "live." and renders the
 * webinar-only LiveApp instead of the app.
 *
 *   live host           app host (old links, redirected)
 *   /                   /live
 *   /<slug>             /live/<slug>
 *   /<slug>/watch?t=..  /live/<slug>/watch?t=..
 */

export const LIVE_ORIGIN = "https://live.uncoveringintimacy.com";

type Loc = Pick<Location, "protocol" | "hostname" | "port">;

const here = (): Loc | undefined =>
  typeof window === "undefined" ? undefined : window.location;

export function isLiveHost(hostname = here()?.hostname ?? ""): boolean {
  return /^live\./i.test(hostname);
}

/**
 * The live host that pairs with the current one: app.X becomes live.X (so a
 * staging host pairs with its own live host), localhost becomes
 * live.localhost on the same port for local testing, anything else (the DO
 * default ingress) the production live host.
 */
export function liveOrigin(loc: Loc | undefined = here()): string {
  if (!loc) return LIVE_ORIGIN;
  const port = loc.port ? `:${loc.port}` : "";
  if (/^app\./i.test(loc.hostname)) {
    return `${loc.protocol}//live.${loc.hostname.slice(4)}${port}`;
  }
  if (loc.hostname === "localhost" || loc.hostname === "127.0.0.1") {
    return `${loc.protocol}//live.localhost${port}`;
  }
  return LIVE_ORIGIN;
}

/** "/live/<slug>/watch" on the app host to "/<slug>/watch" on the live host. */
export function toLivePath(appPathname: string): string {
  const rest = appPathname.replace(/^\/live(?=\/|$)/, "");
  return rest === "" ? "/" : rest;
}

/** "/<slug>/watch" on the live host to the app's route, for the beacon. */
export function toAppPath(livePathname: string): string {
  return livePathname === "/" || livePathname === ""
    ? "/live"
    : `/live${livePathname.replace(/\/+$/, "")}`;
}

/** The registration page's path on whichever host is rendering. */
export function landingPath(slug: string): string {
  const s = `/${encodeURIComponent(slug)}`;
  return isLiveHost() ? s : `/live${s}`;
}
